import moment from 'moment';
import {
  HEALTH_STATUS,
  Healthcheck,
  ControlZone,
  ControlZoneSource,
  ZONE_CONFIG,
  ZONE_KEYS,
  RisksType,
} from 'domain/shared/newProject';
import { StaffingRiskIssue } from 'domain/shared/staffing';

const OUTDATED_RISK_THRESHOLD_DAYS = 20;

export const getOutdatedRisks = (risks: RisksType[]): RisksType[] =>
  risks.filter(risk => {
    const effectiveDate = risk.updatedAt || risk.createdAt;
    if (!effectiveDate) return false;
    return moment().diff(moment(effectiveDate), 'days') >= OUTDATED_RISK_THRESHOLD_DAYS;
  });

export const normalizeControlZone = (zone: ControlZoneSource): ControlZone | null => {
  switch (zone) {
    case 'CRITICAL':
    case ZONE_KEYS.RISK:
      return ZONE_KEYS.RISK;
    case ZONE_KEYS.ESCALATION:
      return ZONE_KEYS.ESCALATION;
    case ZONE_KEYS.MONITORING:
      return ZONE_KEYS.MONITORING;
    case ZONE_KEYS.SAFE:
      return ZONE_KEYS.SAFE;
    default:
      return null;
  }
};

const pickHigherPriorityZone = (current: ControlZone, next: ControlZone): ControlZone =>
  ZONE_CONFIG[next].priority > ZONE_CONFIG[current].priority ? next : current;

export type ResolveControlsZoneArgs = {
  healthChecks?: Healthcheck[];
  healthcheckNames?: string[];
  staffingRiskIssues?: StaffingRiskIssue[];
  staffingRiskIssueTypes?: string[];
};

export const resolveControlsZone = ({
  healthChecks = [],
  healthcheckNames = [],
  staffingRiskIssues = [],
  staffingRiskIssueTypes = [],
}: ResolveControlsZoneArgs): ControlZone => {
  const healthZones = healthChecks
    .filter(check => check.status === HEALTH_STATUS.ACTIVE && !!check.name && healthcheckNames.includes(check.name))
    .map(check => normalizeControlZone(check.targetZone))
    .filter((zone): zone is ControlZone => !!zone);

  const staffingZones = staffingRiskIssues
    .filter(
      issue =>
        issue.status === HEALTH_STATUS.ACTIVE &&
        !!issue.systemIssueType &&
        staffingRiskIssueTypes.includes(issue.systemIssueType),
    )
    .map(issue => normalizeControlZone(issue.targetPriority))
    .filter((zone): zone is ControlZone => !!zone);

  return [...healthZones, ...staffingZones].reduce(pickHigherPriorityZone, ZONE_KEYS.SAFE);
};
